import { useState, useEffect, forwardRef } from 'react';
import { Input } from '@/components/ui/input';
import { parseFR } from '@/lib/utils';

/**
 * NumInputFR — Input numérique tolérant à la virgule française.
 *
 * Problème résolu :
 *   Un input contrôlé qui reçoit `value={numericState}` re-render avec la valeur
 *   convertie. Quand l'utilisateur tape "62,", parseFloat retourne 62 et
 *   React re-render l'input avec "62" (virgule perdue). Il devient impossible
 *   de saisir "62,45".
 *
 * Solution : ce composant maintient un buffer texte local. Il synchronise
 * son affichage avec la valeur numérique du parent UNIQUEMENT quand la valeur
 * parsée diffère (ex : reset externe, chargement initial). Sinon il laisse
 * l'utilisateur taper tranquillement "62,", "62,4", "62,45".
 *
 * Accepte : virgule décimale, espaces, format anglais. Retourne un nombre
 * (0 sur invalide).
 */
const formatDisplay = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  // Ne pas afficher "0" par défaut quand la valeur est 0 mais que l'utilisateur
  // vient d'effacer le champ — on garde la chaîne vide côté UI grâce au buffer.
  // Pour l'affichage initial d'une valeur non nulle : "62.45" → "62,45"
  return String(n).replace('.', ',');
};

const NumInputFR = forwardRef(function NumInputFR(
  { value, onChange, className, placeholder, ...rest },
  ref
) {
  const [buffer, setBuffer] = useState(() => formatDisplay(value));

  // Re-synchroniser le buffer quand la valeur externe change ET diffère du
  // nombre parsé du buffer local (évite d'écraser la saisie en cours).
  useEffect(() => {
    const parsedBuf = parseFR(buffer);
    const num = Number(value) || 0;
    if (Math.abs(parsedBuf - num) > 1e-9) {
      setBuffer(formatDisplay(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={buffer}
      onChange={(e) => {
        const raw = e.target.value;
        // Autoriser vide, chiffres, virgule, point, espace, signe -
        // Bloquer tout autre caractère au niveau visuel pour éviter les surprises.
        if (raw !== '' && !/^-?[\d.,\s]*$/.test(raw)) return;
        setBuffer(raw);
        onChange(parseFR(raw));
      }}
      onBlur={() => {
        // Normaliser l'affichage au blur (ex: "62." → "62")
        setBuffer(formatDisplay(parseFR(buffer)));
      }}
      className={className}
      placeholder={placeholder}
      {...rest}
    />
  );
});

export default NumInputFR;
